import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, HeartHandshake, ShieldCheck, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConnectForm from "../components/ConnectForm";
import usePageMetadata from "../lib/usePageMetadata";

export default function Partners() {
  const { t } = useTranslation();
  const partnershipOptions = [1, 2, 3, 4].map((number) => [t(`partners.option${number}Title`), t(`partners.option${number}Body`)]);
  const principles = [1, 2, 3, 4, 5].map((number) => t(`partners.principle${number}`));
  usePageMetadata({ title: "Brand partnerships | TruBeauté", description: t("partners.metaDescription"), path: "/partners", jsonLd: [{ "@context": "https://schema.org", "@type": "WebPage", name: t("partners.kicker"), description: t("partners.metaDescription"), url: "https://trubeaute.com/partners" }] });

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <main className="pt-32 pb-24 px-6" data-testid="partners-page">
        <section className="max-w-5xl mx-auto text-center mb-20">
          <span className="inline-flex items-center gap-2 text-sm sm:text-base font-medium uppercase tracking-[0.16em] text-[#805557] mb-5">
            <HeartHandshake className="h-4 w-4" /> {t("partners.kicker")}
          </span>
          <h1 className="font-serif text-5xl sm:text-7xl leading-[1.03] text-[#2C2123] mb-6">
            {t("partners.headline")}
          </h1>
          <p className="text-lg sm:text-xl leading-8 text-[#5C4B51] max-w-3xl mx-auto mb-9">
            {t("partners.subline")}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="#partner-inquiry"
              className="inline-flex items-center gap-2 rounded-full bg-[#2C2123] text-white px-7 py-3.5 hover:bg-[#5D4050] transition-colors"
            >
              {t("partners.cta")} <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="/TruBeaute_Brand_Partnership_Deck.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[#9B6A6C] bg-white/70 text-[#805557] px-7 py-3.5 hover:bg-white transition-colors"
            >
              {t("partners.deckCta")}
            </a>
          </div>
        </section>

        <section className="max-w-6xl mx-auto grid md:grid-cols-2 gap-5 mb-20">
          {partnershipOptions.map(([title, body]) => (
            <article key={title} className="rounded-3xl border border-[#EADDD7] bg-white/75 p-7 soft-shadow">
              <Sparkles className="h-5 w-5 text-[#9B6A6C] mb-4" />
              <h2 className="font-serif text-2xl text-[#2C2123] mb-2">{title}</h2>
              <p className="text-[#5C4B51] leading-7">{body}</p>
            </article>
          ))}
        </section>

        <section className="max-w-5xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-start mb-20">
          <div>
            <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C]">{t("partners.pilotKicker")}</span>
            <h2 className="font-serif text-4xl text-[#2C2123] mt-3 mb-5">{t("partners.pilotTitle")}</h2>
            <p className="text-[#5C4B51] leading-8 mb-5">
              {t("partners.pilotBody1")}
            </p>
            <p className="text-[#5C4B51] leading-8">
              {t("partners.pilotBody2")}
            </p>
          </div>
          <div className="rounded-3xl bg-[#2C2123] text-white p-7 sm:p-9">
            <h3 className="font-serif text-2xl mb-5">{t("partners.pilotIncludes")}</h3>
            <ul className="space-y-4 text-[#F7EDEA]">
              {[1, 2, 3, 4, 5].map((number) => t(`partners.pilotItem${number}`)).map((item) => (
                <li key={item} className="flex gap-3"><Check className="h-5 w-5 text-[#D5A8A4] shrink-0 mt-0.5" />{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="max-w-5xl mx-auto rounded-[2rem] border border-[#EADDD7] bg-[#FDF8F5] p-7 sm:p-10 mb-16">
          <div className="flex items-center gap-3 mb-5"><ShieldCheck className="h-6 w-6 text-[#9B6A6C]" /><h2 className="font-serif text-3xl text-[#2C2123]">{t("partners.trustTitle")}</h2></div>
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-[#5C4B51]">
            {principles.map((item) => <li key={item} className="flex gap-3 leading-7"><Check className="h-5 w-5 text-[#9B6A6C] shrink-0 mt-1" />{item}</li>)}
          </ul>
          <p className="mt-7 text-sm text-[#6F6065]">
            {t("partners.readOur")} <Link to="/privacy" className="underline">{t("partners.privacyLink")}</Link> {t("partners.and")} <Link to="/recommendation-methodology" className="underline">{t("partners.methodologyLink")}</Link>.
          </p>
        </section>

        <section className="max-w-3xl mx-auto text-center mb-14">
          <h2 className="font-serif text-4xl text-[#2C2123] mb-4">{t("partners.finalTitle")}</h2>
          <p className="text-[#5C4B51] leading-7 mb-7">{t("partners.finalBody")}</p>
        </section>

        <section id="partner-inquiry" className="max-w-3xl mx-auto scroll-mt-28">
          <div className="text-center mb-8">
            <span className="text-sm sm:text-base font-medium uppercase tracking-[0.16em] text-[#805557]">{t("partners.formKicker")}</span>
            <h2 className="font-serif text-4xl text-[#2C2123] mt-3 mb-4">{t("partners.formTitle")}</h2>
            <p className="text-[#5C4B51] leading-7 max-w-2xl mx-auto">{t("partners.formBody")}</p>
          </div>
          <ConnectForm
            initialRole="partner"
            messagePlaceholderKey="partners.formMessagePlaceholder"
            submitLabelKey="partners.formSubmit"
          />
          <p className="text-sm text-[#6F6065] text-center mt-6">
            {t("partners.formEmailFallback")}{" "}
            <a href="mailto:iyal@trubeaute.com?subject=TruBeaut%C3%A9%20brand%20partnership" className="text-[#805557] underline font-medium">iyal@trubeaute.com</a>
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
